import { forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from "react-router-dom";
import "./UwPolicyExcess.scss";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import newRequest from "../../../utils/newRequest";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TablePagination,
  Tooltip,
} from "@mui/material";
import { showToast } from "../../../toastService.js";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ScaleLoader } from "react-spinners";
import { motion } from "framer-motion";
import { generateUniqueCode, getKeyInArray } from '../../../utils/genericFunctions.js';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdDelete } from 'react-icons/md';


const UwPolicyExcess = forwardRef(({ quoteData, refreshQuote, profileLimitValues, mcCode, scCode, scEndorsed, excessLevel, excessRiskDocid }, ref) => {
  const { t } = useTranslation();
  const { currentUser } = useSelector((state) => state.user);
  const { currentTheme } = useSelector((state) => state.theme);
  const navigate = useNavigate();
  const [refreshDeductibles, setRefreshDeductibles] = useState("N");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPolicyExcess, setFilteredPolicyExcess] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fetchExcess, setFetchExcess] = useState(true);
  const [selectedPolicyExcess, setSelectedPolicyExcess] = useState(null);
  const [policyExcess, setPolicyExcess] = useState([]);
  const [productExcess, setProductExcess] = useState([]);

  // Function to get limit_yn value by limit_code
  const getLimitValue = (limitCode) => {
    try {
      // console.log(`getLimitValue profileLimitValues ${JSON.stringify(profileLimitValues, null, 2)}`);
      const limit = profileLimitValues.find(l => l.limit_code === limitCode);
      return limit ? limit.limit_yn : null;
    } catch (error) {
      console.log(error)
    }
  };

  const fetchPolicyExcess = async () => {
    try {
      const res = await newRequest.post(
        "/uwPolicyExcess/findAll",
        {
          ed_org_code: quoteData.pl_org_code,
          ed_policy_docid: quoteData._id,
          ed_mc_code: mcCode,
          ed_sc_code: scCode,
          ed_level: excessLevel,
          ed_risk_docid: excessLevel === "Risk" ? excessRiskDocid : undefined,
        }
      );
      // console.log(`res.data ${JSON.stringify(res.data, null, 2)}`);
      setPolicyExcess(res.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching excess types:", error);
    }
  };


  const fetchProductExcess = async () => {
    try {
      const res = await newRequest.post(
        "/uwProductExcess/findAll",
        {
          ed_org_code: currentUser.usr_org_code,
          ed_pr_code: quoteData.pl_product_code
        }
      );
      setProductExcess(res.data);
    } catch (error) {
      console.error("Error fetching entity types:", error);
    }
  };


  const handleRemoveExcess = async (e, excess) => {
    try {
      if (excess._id) {
        const res = await newRequest.delete(`/uwPolicyExcess/del/${excess._id}`);
        removeExcessById(excess._id);
      } else {
        fetchPolicyExcess();
      }
    } catch (error) {
      console.error("Error removing condition :", error);
    }
  };

  const removeExcessById = (id) => {
    try {
      const updatedData = policyExcess.filter(item => item._id !== id);
      setPolicyExcess(updatedData);
    } catch (error) {
      console.log(error)
    }
  };


  const handleExcessInputChange = (e, id, field) => {
    try {
      const newExcessData = [...policyExcess];
      const index = newExcessData.findIndex(ex => ex._id === id);
      if (index !== -1) {
        newExcessData[index][field] = e.target.value;
        newExcessData[index]["changed_xx"] = "Y";

        if (field === "ed_type" && e.target.value === "None") {
          newExcessData[index]["ed_value"] = 0;
          newExcessData[index]["ed_limit_fc"] = 0;
          newExcessData[index]["ed_limit_lc"] = 0;
        }

        setPolicyExcess(newExcessData);
      }
    } catch (error) {
      console.log(error);
    }
  };



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 5));
    setPage(0);
  };

  const slicedPolicyExcess = filteredPolicyExcess.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useImperativeHandle(ref, () => ({
    saveExcessFromParent
  }));

  const saveExcessFromParent = async () => {
    try {
      saveExcess("Y");
    } catch (error) {
      console.log(error)
    }
  }

  const saveExcess = async (fromParent = "N") => {
    try {
      // console.log(`policyExcess  ${JSON.stringify(policyExcess, null, 2)}`);
      const changedExcess = policyExcess?.filter(
        (obj) => obj.changed_xx === "Y"
      );

      for (let i = 0; i < changedExcess.length; i++) {
        // console.log(`Now on ${changedExcess.ed_text} flag ${changedExcess[i].flag_xx} changed_xx ${changedExcess[i].changed_xx}`)
        if (changedExcess[i].flag_xx === "new" && changedExcess[i].ed_text) {
          await newRequest.post(
            "/uwPolicyExcess/add",
            {
              _id: undefined,
              ed_risk_note_no: quoteData.pl_risk_note_no,
              ed_policy_docid: quoteData._id,
              ed_end_index: quoteData.pl_end_index,
              ed_text: changedExcess[i].ed_text,
              ed_limit_fc: changedExcess[i].ed_limit_fc,
              ed_limit_lc: changedExcess[i].ed_limit_lc,
              ed_mandatory: "N",
              ed_order: changedExcess[i].ed_order,
              ed_type: changedExcess[i].ed_type,
              ed_value: changedExcess[i].ed_value,
              ed_level: excessLevel,
              ed_risk_docid: excessLevel === "Risk" ? excessRiskDocid : undefined,
              ed_mc_code: mcCode,
              ed_sc_code: scCode,
            }
          );
        } else {
          if (changedExcess[i]?._id) {
            await newRequest.put(
              `/uwPolicyExcess/upd/${changedExcess[i]._id}`,
              {
                ed_text: changedExcess[i].ed_text,
                ed_limit_fc: changedExcess[i].ed_limit_fc,
                ed_limit_lc: changedExcess[i].ed_limit_lc,
                ed_mandatory: changedExcess[i].ed_mandatory,
                ed_order: changedExcess[i].ed_order,
                ed_type: changedExcess[i].ed_type,
                ed_value: changedExcess[i].ed_value
              }
            );
          }
        }
      }

      if (fromParent === "N") {
        refreshQuote();
        // fetchPolicyExcess();
        // setFetchExcess(!fetchExcess);
        setRefreshDeductibles(refreshDeductibles === "N" ? "Y" : "N");
        showToast("Saved!", "success");
      }
    } catch (error) {
      console.error("Error fetching entity types:", error);
    }
  };

  const addExcessRow = () => {
    try {
      // console.log(`policyExcess  ${JSON.stringify(selectedPolicyExcess, null, 2)}`);
      const excessCode = generateUniqueCode();
      setPolicyExcess([...policyExcess, {
        _id: excessCode,
        ed_risk_note_no: quoteData.pl_risk_note_no,
        ed_policy_docid: quoteData._id,
        ed_text: "",
        ed_formatted_text: "",
        ed_limit_fc: 0,
        ed_limit_lc: 0,
        ed_mandatory: "N",
        ed_order: policyExcess?.length + 1,
        sr_no: policyExcess?.length + 1,
        ed_mc_code: mcCode,
        ed_sc_code: scCode,
        ed_type: "None",
        ed_value: 0,
        ed_level: excessLevel,
        ed_risk_docid: excessLevel === "Risk" ? excessRiskDocid : undefined,
        changed_xx: "Y",
        flag_xx: "new",
        ed_end_index: quoteData.pl_end_index,
      }]);
    } catch (error) {
      console.log(error)
    }
  };

  useEffect(() => {
    try {
      fetchPolicyExcess();
    } catch (error) {
      console.log(error)
    }
  }, [refreshDeductibles]);

  useEffect(() => {
    const filteredResults = policyExcess.filter(
      (item) =>
        item.ed_text?.toLowerCase().includes(searchTerm?.toLowerCase())
    );
    setFilteredPolicyExcess(filteredResults);
  }, [policyExcess, searchTerm]);

  return (
    <motion.div
      className="uw-policy-excess"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 1 } }}
      exit={{ opacity: 0, transition: { duration: 1 } }}
    >
      <div className="policy-excess-wrapper">
        <div className="excess-navbar">
          <div className="excess-navbar-left">
            {quoteData.document_enabled_xx === "Y" &&
              scEndorsed === "Y" &&
              <h1 className='main-title'>{t('excessAndDeductibles')}</h1>}
            {quoteData.document_enabled_xx === "N" &&
              policyExcess?.length > 0 &&
              <h1 className='main-title'>{t('excessAndDeductibles')}</h1>}

            {!["RiskNote", "Quote", "QuoteRequest", "Policy", "Renewal", "Opportunity"].includes(mcCode) &&
              quoteData.document_enabled_xx === "Y" &&
              scEndorsed === "Y" &&
              getKeyInArray(quoteData?.endorsement_behavior_xx, "allow_add_deductible") === "Y" &&
              <CircularButton
                currentTheme={currentTheme}
                onClick={addExcessRow}
                icon={<MdAdd />}
                width={"35px"}
                height={"35px"} />}
          </div>
          {["RiskNote", "Quote", "QuoteRequest", "Policy", "Renewal", "Opportunity"].includes(mcCode) &&
            quoteData.document_enabled_xx === "Y" &&
            scEndorsed === "Y" &&
            getKeyInArray(quoteData?.endorsement_behavior_xx, "allow_add_deductible") === "Y" &&
            <div className="excess-navbar-right">
              <CircularButton
                currentTheme={currentTheme}
                onClick={addExcessRow}
                icon={<MdAdd />} width={"30px"} height={"30px"} />
              {/* <CustomLoadingButton
                currentTheme={currentTheme}
                borderRadiusType='none'
                iconPosition='end'
                endIcon={<MdAdd />}
                onClick={addExcessRow}
              >
                {t('add')}
              </CustomLoadingButton> */}
            </div>}
        </div>
        <div className="table-content">
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "200px",
                width: "100%",
                height: "100%",
              }}
            >
              <ScaleLoader
                color="var(--primary)"
                height={50}
                radius={8}
                width={4}
              />
            </div>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ border: 'none', boxShadow: 'none' }}
              className="table-container">
              <Table sx={{ width: "max-content", border: 'none' }}>
                {policyExcess?.length > 0 &&
                  <TableHead className="tablehead">
                    <TableRow className="tablerow">
                      <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                        <span>#</span>
                      </TableCell>
                      <TableCell className="table-cell" style={{ width: "auto" }}>
                        <span>{t('text').toUpperCase()}</span>
                      </TableCell>
                      <TableCell className="table-cell" style={{ maxWidth: "100px", alignItems: "center", textAlign: "center" }}>
                        <span>{t('action').toUpperCase()}</span>
                      </TableCell>
                    </TableRow>
                  </TableHead>}
                <TableBody className="tablebody">
                  {slicedPolicyExcess?.map((excess, index) => (
                    <TableRow
                      key={excess._id}
                      className={`${selectedPolicyExcess?._id === excess._id ? "tablerow current" : "tablerow"}`}
                      style={{ alignItems: "center" }}
                      onClick={() => setSelectedPolicyExcess(excess)}
                    >
                      <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                        {excess?.sr_no}
                      </TableCell>
                      <TableCell className="table-cell" style={{ width: "auto" }}>
                        {quoteData?.document_enabled_xx === "Y" ?
                          <div className="input-item-with-label">
                            <input
                              id="ed_text"
                              value={excess?.ed_text}
                              onChange={(e) => handleExcessInputChange(e, excess._id, 'ed_text')}
                              style={{ width: "100%" }}
                              className="form-input-item table"
                            />
                          </div> :
                          <span className='sub-title'>{excess?.ed_text}</span>
                        }
                      </TableCell>
                      <TableCell className="table-cell" style={{ alignItems: "center", textAlign: "center", padding: "3px", maxWidth: "100px" }}>
                        {quoteData.document_enabled_xx === "Y" &&
                          getKeyInArray(quoteData?.endorsement_behavior_xx, "allow_delete_deductible") === "Y" &&
                          <CircularButton
                            currentTheme={currentTheme}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveExcess(e, excess);
                            }}
                            color="#D32F2F"
                            icon={<MdDelete />}
                            width={"30px"}
                            height={"30px"} />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {policyExcess?.length > rowsPerPage &&
                <TablePagination
                  rowsPerPageOptions={[5]}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  component="div"
                  count={policyExcess?.length}
                  rows={policyExcess}
                  page={page}
                  onPageChange={handleChangePage}
                />}
            </TableContainer>
          )}
        </div>
      </div>
      {
        isProcessing && (
          <motion.div
            className="processing-dialog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
          >
            <div className="processing-wrapper">
              <ScaleLoader
                color="var(--primary)"
                height={50}
                radius={8}
                width={4}
              />
            </div>
          </motion.div>
        )
      }
    </motion.div>
  );
});

export default UwPolicyExcess;
